import React from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

export type SignupStep =
	| "login_register"
	| "business_details"
	| "payment"
	| "confirmation";

const STEPS: { id: SignupStep; label: string }[] = [
	{ id: "login_register", label: "Login/Register" },
	{ id: "business_details", label: "Business Details" },
	{ id: "payment", label: "Payment" },
	{ id: "confirmation", label: "Confirmation" },
];

const TEAL = "#5DBEC3";
const TEAL_DEEP = "#3A9397";
const INACTIVE = "#B5D9DA";

const Wrap = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	width: "100%",
	padding: "32px 24px 24px",
	[theme.breakpoints.down("sm")]: {
		padding: "24px 12px 16px",
	},
}));

const Track = styled(Box)({
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	width: "100%",
	maxWidth: "480px",
	position: "relative",
});

const Node = styled(Box, {
	shouldForwardProp: (p) => p !== "state",
})<{ state: "complete" | "active" | "future" }>(({ state }) => ({
	width: 16,
	height: 16,
	borderRadius: "50%",
	background:
		state === "complete" || state === "active" ? TEAL : "transparent",
	border: `2px solid ${state === "future" ? INACTIVE : TEAL}`,
	boxShadow:
		state === "active" ? `0 0 0 4px rgba(93, 190, 195, 0.25)` : "none",
	zIndex: 2,
	transition: "all 0.2s ease",
}));

const Segment = styled(Box, {
	shouldForwardProp: (p) => p !== "filled",
})<{ filled: boolean }>(({ filled }) => ({
	flex: 1,
	height: 2,
	background: filled ? TEAL : INACTIVE,
	margin: "0 -2px",
	zIndex: 1,
}));

const LabelRow = styled(Box)({
	display: "flex",
	justifyContent: "space-between",
	width: "100%",
	maxWidth: "480px",
	marginTop: 12,
});

const StepLabel = styled(Typography, {
	shouldForwardProp: (p) => p !== "state",
})<{ state: "complete" | "active" | "future" }>(({ state }) => ({
	fontSize: 13,
	fontWeight: state === "active" ? 700 : 500,
	color: state === "future" ? INACTIVE : TEAL_DEEP,
	flex: 1,
	textAlign: "center",
	"&:first-of-type": { textAlign: "left" },
	"&:last-of-type": { textAlign: "right" },
}));

interface Props {
	currentStep: SignupStep;
}

const SignupStepper: React.FC<Props> = ({ currentStep }) => {
	const currentIdx = STEPS.findIndex((s) => s.id === currentStep);
	const stateOf = (i: number): "complete" | "active" | "future" =>
		i < currentIdx ? "complete" : i === currentIdx ? "active" : "future";

	return (
		<Wrap>
			<Track>
				{STEPS.map((s, i) => (
					<React.Fragment key={s.id}>
						<Node state={stateOf(i)} />
						{i < STEPS.length - 1 && (
							<Segment filled={i < currentIdx} />
						)}
					</React.Fragment>
				))}
			</Track>
			<LabelRow>
				{STEPS.map((s, i) => (
					<StepLabel key={s.id} state={stateOf(i)}>
						{s.label}
					</StepLabel>
				))}
			</LabelRow>
		</Wrap>
	);
};

export default SignupStepper;
